import { useState, useCallback, useRef } from "react";
import { motion, useAnimation } from "framer-motion";
import { DollarSign, TrendingUp, BarChart3, Calculator, Sprout, Sparkles } from "lucide-react";

// Extend Window interface for parent window data integration
declare global {
  interface Window {
    reactData?: {
      test?: string;
    };
  }
}

// Self-contained embeddable spinner component
// This component includes all necessary logic and can receive crop data from props

export interface CropData {
  name: string;
  state: string;
  price: number;
  harvestYield: number;
  highYield: number;
  cost: number;
  avgYield: number;
}

export interface EmbedConfig {
  crops?: CropData[];
  primaryColor?: string;
  showInsurance?: boolean;
  onCalculate?: (rent: number, crop: CropData) => void;
}

// Validate and parse crop data from CMS (handles string values)
const validateCrop = (crop: Partial<CropData>): CropData | null => {
  const price = parseFloat(String(crop.price)) || 0;
  const harvestYield = parseFloat(String(crop.harvestYield)) || 0;
  
  // Require minimum valid data
  if (!crop.name || price <= 0 || harvestYield <= 0) {
    return null;
  }
  
  return {
    name: String(crop.name).trim(),
    state: String(crop.state || '').trim(),
    price,
    harvestYield,
    highYield: parseFloat(String(crop.highYield)) || harvestYield * 1.1,
    cost: parseFloat(String(crop.cost)) || 0,
    avgYield: parseFloat(String(crop.avgYield)) || harvestYield * 0.9,
  };
};

// Fallback UI when data is missing or invalid
const MissingDataFallback = () => (
  <div style={{
    padding: '40px',
    textAlign: 'center',
    background: '#FEF2F2',
    borderRadius: '16px',
    border: '1px solid #FECACA',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  }}>
    <p style={{ fontSize: '16px', color: '#991B1B', marginBottom: '8px', margin: '0 0 8px 0' }}>
      Unable to load spinner data
    </p>
    <p style={{ fontSize: '12px', color: '#7F1D1D', margin: 0 }}>
      Check CMS field configuration
    </p>
  </div>
);

// Default crop data if none provided
const defaultCropData: CropData[] = [
  {
    name: "Corn",
    state: "Iowa",
    price: 4.85,
    harvestYield: 185,
    highYield: 210,
    cost: 625,
    avgYield: 175,
  },
  {
    name: "Soybeans",
    state: "Illinois",
    price: 12.50,
    harvestYield: 52,
    highYield: 62,
    cost: 385,
    avgYield: 48,
  },
  {
    name: "Canola",
    state: "North Dakota",
    price: 14.25,
    harvestYield: 42,
    highYield: 50,
    cost: 320,
    avgYield: 38,
  },
];

const RentCurvesSpinner = ({ config }: { config?: EmbedConfig }) => {
  // Read parent window data
  const { test } = window.reactData || {};
  
  // Validate incoming crops data
  const rawCrops = config?.crops || defaultCropData;
  const validatedCrops = rawCrops
    .map(crop => validateCrop(crop))
    .filter((crop): crop is CropData => crop !== null);
  
  // Use default data if no valid crops after validation
  const crops = validatedCrops.length > 0 ? validatedCrops : defaultCropData;
  const SEGMENT_ANGLE = 360 / crops.length;
  
  // Check if we should show fallback (determine before hooks for consistent order)
  const shouldShowFallback = config?.crops && config.crops.length > 0 && validatedCrops.length === 0;

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isSpinning, setIsSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [includeInsurance, setIncludeInsurance] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [rentResult, setRentResult] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const controls = useAnimation();
  const currentCrop = crops[selectedIndex];
  const wheelRef = useRef<SVGSVGElement>(null);
  const dragStartAngle = useRef(0);
  const rotationAtDragStart = useRef(0);
  const lastAngle = useRef(0);
  const velocity = useRef(0);
  const lastTime = useRef(0);

  // Get crop index from rotation angle
  const getCropIndexFromAngle = useCallback((angle: number): number => {
    const normalized = ((angle % 360) + 360) % 360;
    const segmentIndex = Math.floor(((360 - normalized + SEGMENT_ANGLE / 2) % 360) / SEGMENT_ANGLE);
    return segmentIndex % crops.length;
  }, [SEGMENT_ANGLE, crops.length]);

  // Calculate angle from pointer position
  const getAngleFromEvent = useCallback((clientX: number, clientY: number): number => {
    if (!wheelRef.current) return 0;
    const rect = wheelRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    return Math.atan2(clientY - centerY, clientX - centerX) * (180 / Math.PI);
  }, []);

  const handleDragStart = useCallback((e: React.PointerEvent) => {
    if (isSpinning) return;
    const rect = wheelRef.current?.getBoundingClientRect();
    if (rect) {
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const dist = Math.sqrt(Math.pow(e.clientX - centerX, 2) + Math.pow(e.clientY - centerY, 2));
      if (dist < 40) return;
    }
    e.currentTarget.setPointerCapture(e.pointerId);
    setIsDragging(true);
    dragStartAngle.current = getAngleFromEvent(e.clientX, e.clientY);
    rotationAtDragStart.current = rotation;
    lastAngle.current = dragStartAngle.current;
    lastTime.current = Date.now();
    velocity.current = 0;
  }, [isSpinning, rotation, getAngleFromEvent]);

  const handleDragMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging || isSpinning) return;
    const currentAngle = getAngleFromEvent(e.clientX, e.clientY);
    let angleDelta = currentAngle - dragStartAngle.current;
    if (angleDelta > 180) angleDelta -= 360;
    if (angleDelta < -180) angleDelta += 360;
    const newRotation = rotationAtDragStart.current + angleDelta;
    const now = Date.now();
    const timeDelta = now - lastTime.current;
    if (timeDelta > 0) {
      let angleChange = currentAngle - lastAngle.current;
      if (angleChange > 180) angleChange -= 360;
      if (angleChange < -180) angleChange += 360;
      velocity.current = angleChange / timeDelta * 1000;
    }
    lastAngle.current = currentAngle;
    lastTime.current = now;
    setRotation(newRotation);
    controls.set({ rotate: newRotation });
  }, [isDragging, isSpinning, getAngleFromEvent, controls]);

  const handleDragEnd = useCallback(async () => {
    if (!isDragging) return;
    setIsDragging(false);
    const currentVelocity = velocity.current;
    if (Math.abs(currentVelocity) > 50) {
      const momentumRotation = rotation + (currentVelocity * 0.5);
      await controls.start({
        rotate: momentumRotation,
        transition: { duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }
      });
      setRotation(momentumRotation);
      setSelectedIndex(getCropIndexFromAngle(momentumRotation));
    } else {
      setSelectedIndex(getCropIndexFromAngle(rotation));
    }
    setShowResult(false);
    setRentResult(null);
  }, [isDragging, rotation, controls, getCropIndexFromAngle]);

  const handleSpin = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isSpinning || isDragging) return;
    setIsSpinning(true);
    setShowResult(false);
    setRentResult(null);
    const spins = 3 + Math.random() * 3;
    const randomOffset = Math.random() * 360;
    const targetRotation = rotation + (spins * 360) + randomOffset;
    await controls.start({
      rotate: targetRotation,
      transition: { duration: 4, ease: [0.25, 0.1, 0.25, 1] }
    });
    setRotation(targetRotation);
    const winner = getCropIndexFromAngle(targetRotation);
    setSelectedIndex(winner);
    setIsSpinning(false);
  };

  const calculateRent = useCallback(() => {
    const crop = crops[selectedIndex];
    const grossRevenue = crop.price * crop.harvestYield;
    const insuranceDeduction = includeInsurance ? 25 : 0;
    const rent = (grossRevenue - crop.cost - insuranceDeduction) * 0.35;
    return Math.max(0, Number(rent.toFixed(2)));
  }, [crops, selectedIndex, includeInsurance]);

  const handleShowRent = () => {
    setIsCalculating(true);
    setShowResult(false);
    setTimeout(() => {
      const rent = calculateRent();
      setRentResult(rent);
      setShowResult(true);
      setIsCalculating(false);
      config?.onCalculate?.(rent, currentCrop);
    }, 1500);
  };

  // Show fallback if config provided crops but all were invalid (after all hooks)
  if (shouldShowFallback) {
    return <MissingDataFallback />;
  }

  // 3-tier green scheme matching main app (dark, medium, light)
  const segmentColors = [
    "#348C1D",                  // Dark green (100% opacity)
    "rgba(52, 140, 29, 0.4)",   // Medium green (40% opacity)
    "rgba(52, 140, 29, 0.2)",   // Light green (20% opacity)
  ];
  
  // Get text color for contrast: white on dark, dark green on light
  const getTextColor = (index: number): string => {
    const colorIndex = index % segmentColors.length;
    return colorIndex === 0 ? "#ffffff" : "#348C1D";
  };
  
  const size = 400;
  const cx = size / 2;
  const cy = size / 2;
  const radius = size / 2 - 10;

  const createSegmentPath = (index: number) => {
    const startAngle = (index * SEGMENT_ANGLE - 90) * (Math.PI / 180);
    const endAngle = ((index + 1) * SEGMENT_ANGLE - 90) * (Math.PI / 180);
    const x1 = cx + radius * Math.cos(startAngle);
    const y1 = cy + radius * Math.sin(startAngle);
    const x2 = cx + radius * Math.cos(endAngle);
    const y2 = cy + radius * Math.sin(endAngle);
    const largeArc = SEGMENT_ANGLE > 180 ? 1 : 0;
    return `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;
  };

  // Get text position with counter-rotation for "right reading" text
  const getTextPosition = (index: number, wheelRotation: number) => {
    const midAngle = ((index + 0.5) * SEGMENT_ANGLE - 90) * (Math.PI / 180);
    const textRadius = radius * 0.6;
    return {
      x: cx + textRadius * Math.cos(midAngle),
      y: cy + textRadius * Math.sin(midAngle),
      counterRotation: -wheelRotation,
    };
  };

  const inputCards = [
    { label: "Price", value: `$${currentCrop.price.toFixed(2)}`, icon: DollarSign, unit: "/bu" },
    { label: "Harvest Yield", value: currentCrop.harvestYield, icon: TrendingUp, unit: "bu/ac" },
    { label: "High Yield", value: currentCrop.highYield, icon: BarChart3, unit: "bu/ac" },
    { label: "Cost", value: `$${currentCrop.cost}`, icon: Calculator, unit: "/ac" },
    { label: "Avg Yield", value: currentCrop.avgYield, icon: Sprout, unit: "bu/ac" },
  ];

  // Inline styles for self-contained component with CSS isolation
  const styles = {
    container: {
      all: 'initial' as const,  // CSS reset to prevent inheritance
      display: 'block',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      padding: '24px',
      background: '#ffffff',
      borderRadius: '16px',
      maxWidth: '500px',
      margin: '0 auto',
      boxSizing: 'border-box' as const,
      lineHeight: 1.5,
      color: '#374151',
    },
    title: {
      fontSize: '18px',
      fontWeight: 600,
      textAlign: 'center' as const,
      marginBottom: '8px',
      color: '#374151',
    },
    subtitle: {
      fontSize: '14px',
      textAlign: 'center' as const,
      marginBottom: '24px',
      color: '#6B7280',
    },
    wheelContainer: {
      display: 'flex',
      justifyContent: 'center',
      position: 'relative' as const,
    },
    pointer: {
      position: 'absolute' as const,
      top: '-16px',
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 20,
      width: 0,
      height: 0,
      borderLeft: '18px solid transparent',
      borderRight: '18px solid transparent',
      borderTop: '30px solid #2D8C2D',
    },
    cardsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(5, 1fr)',
      gap: '8px',
      marginTop: '24px',
    },
    card: {
      padding: '12px 8px',
      textAlign: 'center' as const,
      background: '#F9FAFB',
      borderRadius: '8px',
      border: '1px solid #E5E7EB',
    },
    cardLabel: {
      fontSize: '10px',
      color: '#6B7280',
      marginBottom: '4px',
    },
    cardValue: {
      fontSize: '14px',
      fontWeight: 700,
      color: '#111827',
    },
    cardUnit: {
      fontSize: '10px',
      color: '#9CA3AF',
    },
    checkboxContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      marginTop: '16px',
    },
    button: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      margin: '24px auto',
      padding: '16px 32px',
      fontSize: '18px',
      fontWeight: 700,
      background: '#2D8C2D',
      color: 'white',
      border: 'none',
      borderRadius: '50px',
      cursor: 'pointer',
    },
    result: {
      textAlign: 'center' as const,
      padding: '24px',
      background: '#F0FDF4',
      borderRadius: '16px',
      border: '1px solid #BBF7D0',
    },
    resultLabel: {
      fontSize: '14px',
      color: '#6B7280',
      marginBottom: '8px',
    },
    resultValue: {
      fontSize: '48px',
      fontWeight: 700,
      color: '#2D8C2D',
    },
    resultUnit: {
      fontSize: '14px',
      color: '#6B7280',
      marginTop: '8px',
    },
    header: {
      fontSize: '28px',
      fontWeight: 700,
      textAlign: 'center' as const,
      marginBottom: '16px',
      color: '#374151',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
  };

  return (
    <>
      <h1 style={styles.header}>{test}</h1>
      <div style={styles.container}>
        <div style={styles.title}>Spin to Select Your Crop</div>
      <div style={styles.subtitle}>Spin for a random crop or drag to select</div>

      <div style={styles.wheelContainer}>
        <div style={styles.pointer} />
        <motion.svg
          ref={wheelRef}
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          style={{ cursor: isDragging ? 'grabbing' : 'grab', userSelect: 'none', touchAction: 'none' }}
          animate={controls}
          initial={{ rotate: rotation }}
          onPointerDown={handleDragStart}
          onPointerMove={handleDragMove}
          onPointerUp={handleDragEnd}
          onPointerLeave={handleDragEnd}
        >
          {/* Wheel border - using inline styles for CSS isolation */}
          <circle 
            cx={cx} 
            cy={cy} 
            r={radius + 5} 
            style={{ fill: 'none', stroke: '#348C1D', strokeWidth: 4 }} 
          />
          {crops.map((crop, index) => {
            const textPos = getTextPosition(index, rotation);
            const textColor = getTextColor(index);
            const textShadow = textColor === "#ffffff" 
              ? "1px 1px 2px rgba(0,0,0,0.5)" 
              : "1px 1px 2px rgba(255,255,255,0.5)";
            return (
              <g key={index}>
                {/* Segment path - inline styles for CSS isolation */}
                <path
                  d={createSegmentPath(index)}
                  style={{ 
                    fill: segmentColors[index % segmentColors.length], 
                    stroke: '#ffffff', 
                    strokeWidth: 2 
                  }}
                />
                <g transform={`translate(${textPos.x}, ${textPos.y}) rotate(${textPos.counterRotation})`}>
                  {/* Crop name text - inline styles for CSS isolation */}
                  <text
                    textAnchor="middle"
                    dominantBaseline="middle"
                    style={{ 
                      fill: textColor, 
                      fontWeight: 'bold', 
                      fontSize: '18px',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      textShadow,
                    }}
                  >
                    {crop.name.toUpperCase()}
                  </text>
                  {/* State text - inline styles for CSS isolation */}
                  <text
                    y="20"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    style={{ 
                      fill: textColor, 
                      fontSize: '13px',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      textShadow,
                    }}
                  >
                    {crop.state}
                  </text>
                </g>
              </g>
            );
          })}
          {/* Center button - inline styles for CSS isolation */}
          <circle
            cx={cx}
            cy={cy}
            r={53}
            style={{ fill: '#348C1D', stroke: '#ffffff', strokeWidth: 3, cursor: 'pointer' }}
            onClick={handleSpin}
          />
          {/* SPIN text - inline styles for CSS isolation */}
          <text
            x={cx}
            y={cy}
            textAnchor="middle"
            dominantBaseline="middle"
            style={{ 
              fill: '#ffffff', 
              fontWeight: 'bold', 
              fontSize: '18px',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              pointerEvents: 'none',
            }}
          >
            {isSpinning ? "..." : "SPIN!"}
          </text>
        </motion.svg>
      </div>

      <div style={styles.cardsGrid}>
        {inputCards.map((card, index) => (
          <div key={index} style={styles.card}>
            <div style={styles.cardLabel}>{card.label}</div>
            <div style={styles.cardValue}>{card.value}</div>
            <div style={styles.cardUnit}>{card.unit}</div>
          </div>
        ))}
      </div>

      {config?.showInsurance !== false && (
        <div style={styles.checkboxContainer}>
          <input
            type="checkbox"
            id="embed-insurance"
            checked={includeInsurance}
            onChange={(e) => {
              setIncludeInsurance(e.target.checked);
              setShowResult(false);
              setRentResult(null);
            }}
          />
          <label htmlFor="embed-insurance" style={{ fontSize: '14px', color: '#6B7280' }}>
            Include crop insurance in calculation
          </label>
        </div>
      )}

      <button
        style={{
          ...styles.button,
          opacity: isCalculating ? 0.7 : 1,
          cursor: isCalculating ? 'wait' : 'pointer',
        }}
        onClick={handleShowRent}
        disabled={isCalculating}
      >
        <Sparkles size={20} />
        {isCalculating ? "Calculating..." : "Show Me Rent!"}
      </button>

      {showResult && rentResult !== null && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          style={styles.result}
        >
          <div style={styles.resultLabel}>Estimated Cash Rent</div>
          <div style={styles.resultValue}>${rentResult.toLocaleString()}</div>
          <div style={styles.resultUnit}>per acre</div>
          <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '16px' }}>
            Based on {currentCrop.name} in {currentCrop.state}
            {includeInsurance && " with crop insurance"}
          </div>
        </motion.div>
      )}
      </div>
    </>
  );
};

export default RentCurvesSpinner;